import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { track } from "../../analytics";
import { Recipe } from "../../types/recipe";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  MousePointer2,
  Hand,
  ArrowRight,
  ArrowLeft,
  X,
  CheckCircle,
  PlayCircle,
  Pointer,
} from "lucide-react";

export interface TutorialCompletionData {
  completed: boolean;
  stepReached: number;
  timestamp: number;
}

export interface OnboardingOverlayProps {
  isFirstVisit: boolean;
  canSkip: boolean;
  recipeCards: Recipe[];
  onComplete: (completionData: TutorialCompletionData) => void;
  onSkip: () => void;
}

export const RecipeDeckOnboardingOverlay: React.FC<OnboardingOverlayProps> = ({
  isFirstVisit,
  canSkip,
  recipeCards,
  onComplete,
  onSkip,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [gestureCompleted, setGestureCompleted] = useState(false);
  const [tutorialVisible, setTutorialVisible] = useState(isFirstVisit);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Accessibility and keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          handleSkip();
          break;
        case "Enter":
        case "ArrowRight":
          advanceStep();
          break;
      }
    },
    [currentStep],
  );

  useEffect(() => {
    if (tutorialVisible) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [tutorialVisible, handleKeyDown]);

  const advanceStep = () => {
    if (currentStep < 5) {
      setCurrentStep((prev) => prev + 1);
      track("tutorial_step_advanced", { step: currentStep + 1 });
    } else {
      completeTutorial();
    }
  };

  const completeTutorial = () => {
    const completionData: TutorialCompletionData = {
      completed: true,
      stepReached: currentStep,
      timestamp: Date.now(),
    };

    track("tutorial_completed", { ...completionData });
    onComplete(completionData);
    setTutorialVisible(false);
  };

  const handleSkip = () => {
    if (canSkip) {
      track("tutorial_skipped", { stepReached: currentStep });
      onSkip();
      setTutorialVisible(false);
    }
  };

  // Gesture detection mock (to be enhanced with actual gesture logic)
  const detectGesture = (gestureType: string) => {
    // Placeholder for actual gesture detection
    setGestureCompleted(true);
  };

  if (!tutorialVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={overlayRef}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        role="dialog"
        aria-label="Recipe Deck Tutorial"
      >
        {/* Tutorial Step Renderer */}
        {renderTutorialStep(currentStep, detectGesture)}

        {/* Navigation Controls */}
        <div className="absolute bottom-8 flex space-x-4">
          {canSkip && currentStep < 5 && (
            <Button
              onClick={handleSkip}
              variant="outline"
              size="lg"
              className="bg-white/90 hover:bg-white text-gray-700 px-6"
            >
              <X className="w-4 h-4 mr-2" />
              Skip Tutorial
            </Button>
          )}
          <Button
            onClick={advanceStep}
            size="lg"
            className="bg-coral hover:bg-coral/90 text-white px-8"
          >
            {currentStep < 5 ? (
              <>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Let's Go!
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Render different tutorial steps with specific instructions
const renderTutorialStep = (
  step: number,
  onGestureDetect: (gestureType: string) => void,
) => {
  type Gesture = 'right' | 'left' | 'up'
  type StepDetail = {
    title: string
    description: string
    icon: React.ReactElement
    gesture?: Gesture
  }

  const stepDetails: Record<number, StepDetail> = {
    1: {
      title: "👋 Welcome to Recipe Discovery!",
      description: "Let's show you how to explore recipes and build your meal plan",
      icon: <PlayCircle className="w-16 h-16 text-coral mx-auto mb-4" />,
    },
    2: {
      title: "Drag Right → I'll Cook This! ✅",
      description: "Drag the recipe card to the right to add it to your meal plan",
      icon: <ArrowRight className="w-16 h-16 text-warning mx-auto mb-4" />,
      gesture: "right",
    },
    3: {
      title: "Drag Left ← Not For Me ❌",
      description: "Drag left to skip recipes you don't want",
      icon: <ArrowLeft className="w-16 h-16 text-gray-400 mx-auto mb-4" />,
      gesture: "left",
    },
    4: {
      title: "Drag Up ↑ Save For Later ⭐",
      description: "Drag up to save recipes you want to try another time",
      icon: <Hand className="w-16 h-16 text-coral mx-auto mb-4 rotate-180" />,
      gesture: "up",
    },
    5: {
      title: "🎉 You're Ready!",
      description: "Try it now - drag a recipe card in any direction!",
      icon: <CheckCircle className="w-16 h-16 text-sage mx-auto mb-4" />,
    },
  };

  const currentStepDetails = stepDetails[step];

  return (
    <motion.div
      key={step}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      role="region"
      aria-live="polite"
      className="max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl"
    >
      {currentStepDetails.icon}
      <h2 className="mb-4 text-2xl font-bold text-gray-800">
        {currentStepDetails.title}
      </h2>
      <p className="text-gray-600 text-lg leading-relaxed">
        {currentStepDetails.description}
      </p>
      
      {/* Visual gesture demonstration */}
      {currentStepDetails.gesture && (
        <motion.div
          className="mt-6 p-4 bg-gray-50 rounded-xl"
          animate={{
            x: currentStepDetails.gesture === "right" ? [0, 30, 0] : 
               currentStepDetails.gesture === "left" ? [0, -30, 0] : 0,
            y: currentStepDetails.gesture === "up" ? [0, -30, 0] : 0,
          }}
          transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: "easeInOut"
          }}
        >
          <div className="w-full h-24 bg-gradient-to-r from-coral to-warning rounded-lg flex items-center justify-center text-white font-medium">
            Recipe Card
          </div>
        </motion.div>
      )}

      {/* Step indicator */}
      <div className="mt-6 flex justify-center gap-2">
        {[1, 2, 3, 4, 5].map((s) => (
          <div
            key={s}
            className={`h-2 w-2 rounded-full transition-all ${
              s === step ? "bg-coral w-8" : "bg-gray-300"
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default RecipeDeckOnboardingOverlay;
